import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, Loader2, GitBranch, Building, User, Tag, Shield,
  Zap, Clock, BarChart3, FileText, Map, Video, BookOpen, Lightbulb,
  FolderTree, Eye, Edit, Trash2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import HelpTip from '../components/HelpTip';
import { ProcessTree, LEVEL_COLORS, STATUS_COLORS, TYPE_COLORS } from '../components/ProcessTree';
import { cn } from '../lib/utils';

const LEVEL_NAMES = { 1: "Enterprise", 2: "Value Stream", 3: "End-to-End Process", 4: "Sub-Process", 5: "Task" };
const ARTEFACT_ICONS = { ideas: Lightbulb, projects: FolderTree, process_maps: Map, captures: Video, sops: BookOpen };

const Processes = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: '', description: '', level: '3', parent_process_id: '',
    departments: [], process_owner: '', status: 'Draft', process_type: 'Core',
    tags: '', criticality: 'Medium', risk_rating: 'Low', automation_potential: 'None',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    try {
      const procRes = await api.get('/processes/tree');
      setProcesses(procRes.data);
      // Use L2 processes as department options
      setDepartments(procRes.data.filter(p => p.level === 2).map(p => ({ id: p.id, name: p.name })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (selectedId) {
      api.get(`/processes/${selectedId}`).then(r => setSelectedProcess(r.data)).catch(() => setSelectedProcess(null));
    } else {
      setSelectedProcess(null);
    }
  }, [selectedId]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        level: parseInt(form.level),
        parent_process_id: form.parent_process_id || null,
        departments: form.departments,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      const res = await api.post('/processes', payload);
      toast.success('Process created');
      setShowCreateDialog(false);
      setForm(emptyForm);
      await fetchAll();
      setSelectedId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create process');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        level: parseInt(form.level),
        parent_process_id: form.parent_process_id || null,
        departments: form.departments,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      await api.put(`/processes/${selectedId}`, payload);
      toast.success('Process updated');
      setShowEditDialog(false);
      await fetchAll();
      // Refresh detail
      const r = await api.get(`/processes/${selectedId}`);
      setSelectedProcess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this process? Processes with linked artefacts will be retired instead.')) return;
    try {
      const res = await api.delete(`/processes/${selectedId}`);
      toast.success(res.data.message);
      setSelectedId(null);
      await fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEditDialog = () => {
    const p = selectedProcess;
    if (!p) return;
    setForm({
      name: p.name || '', description: p.description || '', level: String(p.level),
      parent_process_id: p.parent_process_id || '', departments: p.departments || [],
      process_owner: p.process_owner || '', status: p.status || 'Draft',
      process_type: p.process_type || 'Core', tags: (p.tags || []).join(', '),
      criticality: p.criticality || 'Medium', risk_rating: p.risk_rating || 'Low',
      automation_potential: p.automation_potential || 'None',
    });
    setShowEditDialog(true);
  };

  const openCreateDialog = (parentId = null, parentLevel = 0) => {
    setForm({
      ...emptyForm,
      parent_process_id: parentId || '',
      level: parentId ? String(Math.min(parentLevel + 1, 5)) : '1',
    });
    setShowCreateDialog(true);
  };

  const filteredProcesses = search
    ? processes.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))
      )
    : processes;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-[#235894]" /></div>;
  }

  const p = selectedProcess;

  return (
    <div className="animate-fade-in" data-testid="processes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Processes</h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center gap-1">Process architecture and hierarchy management <HelpTip helpKey="processes.page" /></p>
        </div>
        <Button onClick={() => openCreateDialog()} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="create-process-btn">
          <Plus className="w-4 h-4 mr-2" /> New Process
        </Button>
      </div>

      <div className="flex gap-6" style={{ minHeight: 'calc(100vh - 220px)' }}>
        {/* Left: Tree */}
        <Card className="w-80 flex-shrink-0 overflow-hidden">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search processes..." className="pl-9 h-8 text-sm" data-testid="process-search" />
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            <ProcessTree processes={filteredProcesses} selectedId={selectedId} onSelect={setSelectedId} />
          </CardContent>
        </Card>

        {/* Right: Detail panel */}
        <div className="flex-1 min-w-0">
          {!p ? (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-16">
                <GitBranch className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Select a process</h3>
                <p className="text-slate-400 text-sm mb-4">Choose from the hierarchy tree or create a new process</p>
                <Button onClick={() => openCreateDialog()} variant="outline"><Plus className="w-4 h-4 mr-2" /> Create Process</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Process Header */}
              <Card>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg', LEVEL_COLORS[p.level])}>{p.level}</span>
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">{p.name}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={STATUS_COLORS[p.status]}>{p.status}</Badge>
                          <Badge variant="outline" className={TYPE_COLORS[p.process_type]}>{p.process_type}</Badge>
                          <span className="text-sm text-slate-400">{LEVEL_NAMES[p.level]} (L{p.level})</span>
                          <span className="text-sm text-slate-400">v{p.version}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/processes/${p.id}`)} data-testid="view-detail-btn"><Eye className="w-4 h-4 mr-1" /> Detail</Button>
                      <Button size="sm" variant="outline" onClick={openEditDialog}><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => openCreateDialog(p.id, p.level)}><Plus className="w-4 h-4 mr-1" /> Add Child</Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDelete}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {p.description && <p className="text-slate-600 mt-3 text-sm">{p.description}</p>}
                </CardContent>
              </Card>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 text-sm"><Building className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-400">Departments</p><p className="font-medium">{p.departments?.join(', ') || '-'}</p></div></div>
                </CardContent></Card>
                <Card><CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-400">Process Owner</p><p className="font-medium">{p.process_owner || '-'}</p></div></div>
                </CardContent></Card>
                <Card><CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-400">Criticality / Risk</p><p className="font-medium">{p.criticality} / {p.risk_rating}</p></div></div>
                </CardContent></Card>
                <Card><CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 text-sm"><Zap className="w-4 h-4 text-slate-400" /><div><p className="text-xs text-slate-400">Automation Potential</p><p className="font-medium">{p.automation_potential}</p></div></div>
                </CardContent></Card>
              </div>

              {/* Tags */}
              {p.tags?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {p.tags.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                </div>
              )}

              {/* Children */}
              {p.children?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500 uppercase tracking-wider">Child Processes ({p.children.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {p.children.map(c => (
                      <div key={c.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm" onClick={() => setSelectedId(c.id)}>
                        <span className={cn('w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold', LEVEL_COLORS[c.level])}>{c.level}</span>
                        <span className="font-medium flex-1">{c.name}</span>
                        <Badge className={cn('text-xs', STATUS_COLORS[c.status])}>{c.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Linked Artefacts Summary */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500 uppercase tracking-wider">Linked Artefacts ({p.linked_counts?.total || 0})</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(ARTEFACT_ICONS).map(([key, Icon]) => {
                      const count = p.linked_counts?.[key] || 0;
                      return (
                        <div key={key} className={cn('text-center p-3 rounded-lg border', count > 0 ? 'bg-[#235894]/5 border-[#235894]/20' : 'bg-slate-50 border-slate-100')}>
                          <Icon className={cn('w-5 h-5 mx-auto mb-1', count > 0 ? 'text-[#235894]' : 'text-slate-300')} />
                          <p className="text-lg font-bold">{count}</p>
                          <p className="text-xs text-slate-500 capitalize">{key.replace('_', ' ')}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Audit */}
              <div className="flex items-center gap-4 text-xs text-slate-400 px-1">
                <span>Created: {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by {p.created_by_name}</span>
                <span>Modified: {new Date(p.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      {(showCreateDialog || showEditDialog) && (
        <Dialog open={true} onOpenChange={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{showEditDialog ? 'Edit Process' : 'Create Process'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Invoice Processing" data-testid="process-name-input" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Level *</Label>
                  <Select value={form.level} onValueChange={v => setForm(f => ({ ...f, level: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5].map(l => <SelectItem key={l} value={String(l)}>L{l} - {LEVEL_NAMES[l]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Parent Process</Label>
                  <Select value={form.parent_process_id || 'none'} onValueChange={v => setForm(f => ({ ...f, parent_process_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="None (root)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (root)</SelectItem>
                      {processes.filter(pp => pp.level < parseInt(form.level) && pp.id !== selectedId).map(pp => (
                        <SelectItem key={pp.id} value={pp.id}>L{pp.level} {pp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type</Label>
                  <Select value={form.process_type} onValueChange={v => setForm(f => ({ ...f, process_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['Core','Support','Management'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['Draft','Active','Retired'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Departments</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {departments.map(d => (
                    <label key={d.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.departments.includes(d.name)} onChange={e => {
                        setForm(f => ({ ...f, departments: e.target.checked ? [...f.departments, d.name] : f.departments.filter(x => x !== d.name) }));
                      }} className="rounded" />
                      {d.name}
                    </label>
                  ))}
                </div>
              </div>
              <div><Label>Process Owner</Label><Input value={form.process_owner} onChange={e => setForm(f => ({ ...f, process_owner: e.target.value }))} placeholder="e.g. Sarah Chen" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Criticality</Label>
                  <Select value={form.criticality} onValueChange={v => setForm(f => ({ ...f, criticality: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['Low','Medium','High'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Risk Rating</Label>
                  <Select value={form.risk_rating} onValueChange={v => setForm(f => ({ ...f, risk_rating: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['Low','Medium','High'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Automation</Label>
                  <Select value={form.automation_potential} onValueChange={v => setForm(f => ({ ...f, automation_potential: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['None','Low','Medium','High'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. finance, automation" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>Cancel</Button>
              <Button onClick={showEditDialog ? handleUpdate : handleCreate} disabled={saving} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="save-process-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {showEditDialog ? 'Save Changes' : 'Create Process'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Processes;
